'use client';

import { useCallback, useState } from 'react';
import { fetchSeedingItemDetail } from '@/api/marketingPages';
import { SeedingItemDetailResponse } from '@/app/(activity)/campaigns/[slug]/types';
import logger from '@/utils/client-logger';

interface UseSeedingItemDetailOptions {
  marketingPageId: number;
  isPreview: boolean;
}

interface UseSeedingItemDetailReturn {
  detail: SeedingItemDetailResponse | null;
  loading: boolean;
  fetchDetail: (itemId: number) => Promise<SeedingItemDetailResponse | null>;
  reset: () => void;
}

export function useSeedingItemDetail({
  marketingPageId,
  isPreview,
}: UseSeedingItemDetailOptions): UseSeedingItemDetailReturn {
  const [detail, setDetail] = useState<SeedingItemDetailResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchDetail = useCallback(
    async (itemId: number) => {
      if (marketingPageId <= 0 || itemId <= 0) return null;

      setLoading(true);
      try {
        const response = await fetchSeedingItemDetail(itemId, marketingPageId, isPreview);
        setDetail(response);
        return response;
      } catch (error) {
        logger.error(error, 'Failed to fetch seeding item detail:');
        return null;
      } finally {
        setLoading(false);
      }
    },
    [marketingPageId, isPreview],
  );

  const reset = useCallback(() => {
    setDetail(null);
  }, []);

  return {
    detail,
    loading,
    fetchDetail,
    reset,
  };
}
